/**
 * Persistent, toggleable "script view" column.
 *
 * In the sidebar-driven layout the screenplay is no longer a panel you
 * navigate *to* — it's a reference column that sits between the sidebar
 * and the active tool and can be shown / hidden at will (left → right:
 * sidebar → script view → feature panel → timeline dock at the bottom).
 *
 * It wraps a single, always-mounted `ScriptEditorPanel` instance so its
 * DOM (and the `[data-koko-action]` controls Koko's choreography drives)
 * is present whenever the column is open. The column can be:
 *   - shown / hidden  (the sidebar "Script" toggle + the × close button),
 *   - resized in width (drag the right-edge handle), and
 *   - maximized to fill the whole work area, hiding the feature panel
 *     (the ⤢ button) — so you can work in the script alone.
 * Open state, width, and maximized state all persist to localStorage.
 *
 * Because the script editor lives here instead of the PanelManager, the
 * manager delegates `open("script_editor")` to this dock via an external
 * opener (see `main.ts`), keeping every existing call site — directives,
 * the upload panel's "start writing", timeline jumps — working unchanged.
 */

import { ScriptEditorPanel } from "./panels/ScriptEditorPanel";
import type { PanelManager } from "./PanelManager";
import type { StudioStore } from "./StudioStore";
import "./styles/script-dock.css";

const UI_STATE_KEY = "koko_studio_script_ui";

/** Width clamps for the resizable column. */
const MIN_WIDTH = 280;
const DEFAULT_WIDTH = 380;
/** Space protected for the compact rail and functional main workspace. */
const SIDEBAR_RAIL_WIDTH = 64;
const MIN_MAIN_WIDTH = 520;
const TREE_GUTTER_WIDTH = 200;
const TREE_GUTTER_MIN_VIEWPORT = 1480;
const MOBILE_BREAKPOINT = 860;

interface ScriptDockUiState {
  open: boolean;
  width: number;
  maximized: boolean;
}

interface ScriptDockOptions {
  store: StudioStore;
  workerBaseUrl: string;
  panelManager: PanelManager;
  /** Fired whenever the column is shown / hidden (keeps the sidebar toggle in sync). */
  onStateChange?: (open: boolean) => void;
}

export class ScriptDock {
  private readonly root: HTMLElement;
  private readonly panel: ScriptEditorPanel;
  private readonly panelManager: PanelManager;
  private readonly onStateChange?: (open: boolean) => void;
  private readonly maxButton: HTMLButtonElement;
  private readonly resizeHandle: HTMLElement;

  private open = false;
  private width = DEFAULT_WIDTH;
  private maximized = false;

  // Resize-drag bookkeeping.
  private dragging = false;
  private dragPointerId: number | null = null;
  private dragStartX = 0;
  private dragStartWidth = 0;
  private readonly onViewportResize = (): void => this.applyState();

  constructor(options: ScriptDockOptions) {
    this.panelManager = options.panelManager;
    this.onStateChange = options.onStateChange;
    this.loadUiState();

    this.root = document.createElement("aside");
    this.root.className = "studio-script-dock";
    this.root.dataset.testid = "studio-script-dock";
    this.root.setAttribute("aria-label", "Script");

    const header = document.createElement("div");
    header.className = "studio-script-dock-header";

    const title = document.createElement("span");
    title.className = "studio-script-dock-title";
    title.textContent = "script";

    const importButton = document.createElement("button");
    importButton.type = "button";
    importButton.className = "studio-script-dock-action";
    importButton.textContent = "import pdf";
    importButton.title = "Upload a screenplay PDF";
    importButton.dataset.kokoAction = "script-import";
    importButton.addEventListener("click", () =>
      this.panelManager.open("script_upload"),
    );

    this.maxButton = document.createElement("button");
    this.maxButton.type = "button";
    this.maxButton.className = "studio-script-dock-max";
    this.maxButton.dataset.kokoAction = "script-maximize";
    this.maxButton.addEventListener("click", () => this.toggleMaximized());

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "studio-script-dock-close";
    closeButton.textContent = "×";
    closeButton.title = "Hide the script column";
    closeButton.setAttribute("aria-label", "Hide the script column");
    closeButton.addEventListener("click", () => this.setOpen(false));

    header.appendChild(title);
    header.appendChild(importButton);
    header.appendChild(this.maxButton);
    header.appendChild(closeButton);

    const body = document.createElement("div");
    body.className = "studio-script-dock-body";

    // Right-edge drag handle for resizing the column width.
    const resize = document.createElement("div");
    resize.className = "studio-script-dock-resize";
    resize.setAttribute("role", "separator");
    resize.setAttribute("aria-orientation", "vertical");
    resize.setAttribute("aria-label", "Resize script width");
    resize.tabIndex = 0;
    this.resizeHandle = resize;
    resize.title = "Drag to resize the script column";
    this.wireResize(resize);

    this.root.appendChild(header);
    this.root.appendChild(body);
    this.root.appendChild(resize);

    // Mount the screenplay once and keep it shown — visibility is driven
    // by the column's own `is-closed` class, not the panel lifecycle.
    this.panel = new ScriptEditorPanel({
      store: options.store,
      workerBaseUrl: options.workerBaseUrl,
    });
    this.panel.mount(body);
    this.panel.show();

    window.addEventListener("resize", this.onViewportResize);
    this.applyState();
  }

  /** The element to mount into the workspace row. */
  getElement(): HTMLElement {
    return this.root;
  }

  /** Return a manually maximized script to the normal split workspace. */
  restoreSplit(): void {
    if (!this.maximized) return;
    this.maximized = false;
    this.persistUiState();
    this.applyState();
  }

  isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.setOpen(!this.open);
  }

  setOpen(open: boolean): void {
    if (this.open === open) {
      // Already in the requested state — still re-show so a fresh
      // open() lands on the latest scenes.
      if (open) this.panel.show();
      return;
    }
    this.open = open;
    this.applyState();
    this.persistUiState();
    if (open) this.panel.show();
    this.onStateChange?.(open);
  }

  private toggleMaximized(): void {
    this.maximized = !this.maximized;
    this.applyState();
    this.persistUiState();
  }

  destroy(): void {
    window.removeEventListener("resize", this.onViewportResize);
    this.panel.destroy();
  }

  // ------------------------------------------------------------------
  // Resize
  // ------------------------------------------------------------------

  private maxWidth(): number {
    const treeGutter = window.innerWidth >= TREE_GUTTER_MIN_VIEWPORT
      ? TREE_GUTTER_WIDTH
      : 0;
    return Math.max(
      MIN_WIDTH,
      window.innerWidth - SIDEBAR_RAIL_WIDTH - treeGutter - MIN_MAIN_WIDTH,
    );
  }

  private isFocusedLayout(): boolean {
    return (
      window.innerWidth <= MOBILE_BREAKPOINT ||
      window.innerWidth - SIDEBAR_RAIL_WIDTH - MIN_MAIN_WIDTH < MIN_WIDTH
    );
  }

  private setWidth(next: number): void {
    this.width = clamp(next, MIN_WIDTH, this.maxWidth());
    this.root.style.setProperty("--script-dock-width", `${this.width}px`);
    this.syncResizeAccessibility();
  }

  private syncResizeAccessibility(): void {
    this.resizeHandle.setAttribute("aria-valuemin", String(MIN_WIDTH));
    this.resizeHandle.setAttribute("aria-valuemax", String(this.maxWidth()));
    this.resizeHandle.setAttribute(
      "aria-valuenow",
      String(clamp(this.width, MIN_WIDTH, this.maxWidth())),
    );
  }

  private wireResize(handle: HTMLElement): void {
    handle.addEventListener("pointerdown", (event) => {
      // No edge-resize while maximized (the column owns the whole area).
      if (this.maximized || this.isFocusedLayout()) return;
      this.dragging = true;
      this.dragPointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartWidth = this.root.offsetWidth;
      handle.setPointerCapture(event.pointerId);
      this.root.classList.add("is-resizing");
      event.preventDefault();
    });

    handle.addEventListener("pointermove", (event) => {
      if (!this.dragging) return;
      this.setWidth(clamp(
        this.dragStartWidth + (event.clientX - this.dragStartX),
        MIN_WIDTH,
        this.maxWidth(),
      ));
    });

    const end = (): void => {
      if (!this.dragging) return;
      this.dragging = false;
      this.root.classList.remove("is-resizing");
      if (this.dragPointerId !== null) {
        try {
          handle.releasePointerCapture(this.dragPointerId);
        } catch {
          // Already released — ignore.
        }
      }
      this.dragPointerId = null;
      this.persistUiState();
    };
    handle.addEventListener("pointerup", end);
    handle.addEventListener("pointercancel", end);
    handle.addEventListener("keydown", (event) => {
      if (this.maximized || this.isFocusedLayout()) return;
      let next: number | null = null;
      if (event.key === "ArrowLeft") next = this.width - 16;
      if (event.key === "ArrowRight") next = this.width + 16;
      if (event.key === "Home") next = MIN_WIDTH;
      if (event.key === "End") next = this.maxWidth();
      if (next === null) return;
      event.preventDefault();
      this.setWidth(next);
      this.persistUiState();
    });
  }

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  private applyState(): void {
    const focused = this.isFocusedLayout();
    const renderedWidth = clamp(this.width, MIN_WIDTH, this.maxWidth());
    this.root.classList.toggle("is-closed", !this.open);
    this.root.classList.toggle("is-max", this.maximized);
    this.root.classList.toggle("is-focused", focused);
    this.root.dataset.state = this.open ? "open" : "closed";
    this.root.dataset.layout = focused ? "focused" : "split";
    this.root.style.setProperty("--script-dock-width", `${renderedWidth}px`);
    this.syncResizeAccessibility();
    this.maxButton.textContent = this.maximized ? "⤡" : "⤢";
    this.maxButton.title = this.maximized
      ? "Restore the script column width"
      : "Expand the script to fill the work area";
    this.maxButton.setAttribute("aria-label", this.maxButton.title);
    this.maxButton.setAttribute("aria-pressed", this.maximized ? "true" : "false");
  }

  private loadUiState(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    const raw = window.localStorage.getItem(UI_STATE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<ScriptDockUiState>;
      if (typeof parsed.open === "boolean") this.open = parsed.open;
      if (typeof parsed.width === "number" && Number.isFinite(parsed.width)) {
        this.width = Math.max(parsed.width, MIN_WIDTH);
      }
      if (typeof parsed.maximized === "boolean") this.maximized = parsed.maximized;
    } catch {
      // Corrupt entry — fall back to defaults.
    }
  }

  private persistUiState(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      const state: ScriptDockUiState = {
        open: this.open,
        width: this.width,
        maximized: this.maximized,
      };
      window.localStorage.setItem(UI_STATE_KEY, JSON.stringify(state));
    } catch {
      // Quota — silently ignore.
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
